import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Spinner, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import useApproveRight, { APPROVE_DENIED_MESSAGE } from "../../utils/useApproveRight";
import { ErrorMessage, FieldArray, Formik, Field } from "formik";
import { useSelector } from "react-redux";
import {
  getAllBankListForDropdown,
  getAllItemMasterForDropdown,
  getStockRequestById,
  getAllAccountByBankId,
  getAllPaymentModes,
  postRejectStockRequest,
  getPreviousStockRequestById,
  getAllPreviousItemsList,
  getAllPreviousItemsStockList,
} from "../../services/contractorApi";
import { Helmet } from "react-helmet";
import CardComponent from "../../components/CardComponent";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TransferStockSchema } from "../../utils/formSchema";
import {
  postRescheduledTransferStock,
  postStockTransferRequest,
  updateStockTransferRequest,
} from "../../services/contractoApi2";
import { BsQuestionLg } from "react-icons/bs";
import ReactTextareaAutosize from "react-textarea-autosize";
import TextareaAutosize from "react-textarea-autosize";
import { addRemarkSchema } from "../../utils/formSchema";
import ImageViewer from "../../components/ImageViewer";
import ConfirmAlert from "../../components/ConfirmAlert";
import { ViewFundRequest } from "../FundManagement/FundRequest/ViewFundRequest";
import StockTableView from "./StockTableView";
import { GroupTable } from "../../components/GroupTable";
import { ViewStockRequestDetails } from "../StockRequest/ViewStockRequestDetails";
import { useTranslation } from "react-i18next";
import { MdEdit } from "react-icons/md";
import DatePickercomp from "../../components/DatePicker";
import { formatNumberToINR, formatAmountINR, to3 } from "../../utils/helper";

const CreateStockTransfer = () => {
  const user_id = useSelector((state) => state.auth.user.id);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || null;
  // Approve right of this screen (sidebar tree); the API enforces it too.
  const { canApprove } = useApproveRight();
  const [edit, setEdit] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [showRescheduled, setShowRescheduled] = useState();
  const [refresh, setRefresh] = useState(false);
  const [itemMasterData, setItemMasterData] = useState([]);
  const [allBanksData, setAllBanksData] = useState([]);
  const [allAccountByBankId, setAllAccountByBankId] = useState([]);
  const [accountBalance, setAccountBalance] = useState({ balance: 0 });
  const [showRejectAlert, setShowRejectAlert] = useState(false);
  const [storeId, setStoreId] = useState({});
  const [previousStockAmount, setpreviousStockAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState([]);
  const [allOldItems, setAllOldItems] = useState([]);
  const [bill_amount, setBill_Amount] = useState(0);
  // The "All Existing Items" list is scoped to the selected transfer date
  // (backend filters stocks.created_at <= transfer_date). We keep the
  // requested_for user id so we can RE-FETCH the list whenever the transfer
  // date changes. The list stays empty until a date is picked.
  const [requestedForId, setRequestedForId] = useState(null);
  const [transferDateForItems, setTransferDateForItems] = useState("");

  // Re-entrancy guard: blocks a second/third submit that fires before the first
  // request resolves (rapid double/triple-click on the Confirm button). The
  // backend's transaction_id/15-sec guards can't see same-instant concurrent
  // requests (single shared DB connection), so they were slipping through and
  // creating duplicate/triplicate paired ledger rows for ONE supplier payment.
  const isSubmittingRef = useRef(false);

  // const [userType, setUserType] = useState("");
  const { t } = useTranslation();

  const userTypeOptions = [
    { label: "User", value: "user" },
    { label: "Supplier", value: "supplier" },
  ];

  useEffect(() => {
    fetchPaymentModes();
  }, []);

  // Who the request is for, as the detail API names it (requested_for_*),
  // with the older request_for_* keys as a fallback.
  const forName = edit?.requested_for_name || edit?.request_for;
  const forEmpId = edit?.requested_for_employee_id || edit?.request_for_employee_id;
  const forImage = edit?.requested_for_image || edit?.request_for_image;

  const fetchSingleData = async () => {
    const res = await getStockRequestById(id);
    if (res.status) {
      setEdit(res.data);

      getPreviousStockRequestDetails(res.data?.requested_for);
      // Remember the user; the existing-items list is fetched only once a
      // transfer date is chosen (see fetchAllPreviousItem / the date-change
      // effect below).
      setRequestedForId(res.data?.requested_for);
      // On a genuine edit (a transfer date was already saved), seed the
      // as-of date so the existing-items list loads scoped to it. New
      // transfers leave it empty until the user picks a date.
      if (res.data?.stock_transfer_date) {
        const d = parseCustomDate(res.data.stock_transfer_date);
        if (d && !isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, "0");
          setTransferDateForItems(
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
          );
        }
      }
    } else {
      setEdit({});
    }
  };

  // Pass the selected transfer date so the backend returns only items received
  // (stocks.created_at) on/before that date. Until a date is picked the list is
  // cleared/empty so a stale, unscoped list can't be acted on.
  const fetchAllPreviousItem = async (userId, transferDate) => {
    if (!userId || !transferDate) {
      setAllOldItems([]);
      return;
    }
    const res = await getAllPreviousItemsStockList(userId, transferDate);
    if (res.status) {
      setAllOldItems(res.data);
    } else {
      setAllOldItems([]);
    }
  };

  // Re-fetch the existing-items list whenever the user OR the selected transfer
  // date changes, so it reflects items received as-of that date.
  useEffect(() => {
    fetchAllPreviousItem(requestedForId, transferDateForItems);
  }, [requestedForId, transferDateForItems]);

  const fetchPaymentModes = async () => {
    const res = await getAllPaymentModes();
    if (res.status) {
      const rData = res.data.map((d) => {
        return { label: d.method, value: d.method };
      });

      setPaymentMode(rData);
    } else {
      setEdit({});
    }
  };

  const getPreviousStockRequestDetails = async (id) => {
    const res = await getPreviousStockRequestById(id);
    if (res.status) {
      setpreviousStockAmount(res?.data);
    } else {
      setpreviousStockAmount(0);
    }
  };

  const formatDateTime = (dateVal) => {
    if (!dateVal) return "";

    const d = new Date(dateVal);

    const pad = (n) => String(n).padStart(2, "0");

    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  };

  //   submit form
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!canApprove) {
      toast.error(APPROVE_DENIED_MESSAGE);
      return;
    }
    // Block duplicate in-flight submits (double/triple-click) BEFORE any async
    // work, even within the same render tick where isSubmitting hasn't flipped
    // yet. One real payment must create exactly one ledger entry.
    if (isSubmittingRef.current) {
      return;
    }
    if (!values.stock_transfer_date) {
      toast.error("Please select stock transfer date");
      setSubmitting(false);
      return;
    }
    isSubmittingRef.current = true;
    console.log("RAW DATE =", values.stock_transfer_date);
    console.log("FORMATTED =", formatDateTime(values.stock_transfer_date));
    console.log("DATE TYPE", typeof values.stock_transfer_date);
    console.log("DATE VAL", values.stock_transfer_date);

    const modifiedRequest = values?.transfer_data?.map((item) => {
      return {
        ...item,
        // total_transfer_amount:finalRequestAmount,
        // transfer_fund: item.transfer_fund,
        // new_transfer_fund: item.new_transfer_fund,
      };
    });

    const sData = {
      id: edit?.id,
      remark: values?.remark,
      payment_mode: values.payment_mode,
      // stock_transfer_date: values?.stock_transfer_date,
      transfer_date: values.stock_transfer_date
        ? formatDateTime(values.stock_transfer_date)
        : null,
      bill_number: values?.bill_number,
      bill_date: values?.bill_date,
      // bill_amount: values?.bill_amount,
      bill_amount: bill_amount,
      transaction_id: values?.transaction_id,
      // account_id: values?.account_id?.value || "5",
      account_id: values?.account_id?.value || 5,
      transfer_data: modifiedRequest,
    };

    // return console.log("sdata === ", sData);

    const res = showRescheduled
      ? await postRescheduledTransferStock(edit?.id, values?.rescheduled_date)
      : type == "update"
        ? await updateStockTransferRequest(sData)
        : await postStockTransferRequest(sData);

    if (res.status) {
      toast.success(res.message);
      resetForm();
      navigate(-1);
    } else {
      toast.error(res.message);
    }
    setSubmitting(false);
    // Release the guard so a legitimate retry (e.g. after a failed/invalid
    // response) is allowed.
    isSubmittingRef.current = false;
  };

  const fetchItemMasterData = async () => {
    const res = await getAllItemMasterForDropdown();
    if (res.status) {
      setItemMasterData(res.data);
    } else {
      setItemMasterData([]);
    }
  };

  // const fetchAllBanksData = async () => {
  //   const res = await getAllBankListForDropdown();
  //   if (res.status) {
  //     const rData = res.data.map((itm) => {
  //       return {
  //         value: itm?.id,

  //         label: itm?.bank_name,
  //         logo: itm?.logo,
  //       };
  //     });
  //     setAllBanksData(rData);
  //   } else {
  //     setAllBanksData([]);
  //   }
  // };

  const fetchAllBanksData = async () => {
    const res = await getAllBankListForDropdown({ isAccounts: true });

    if (res.status) {
      const rData = res.data.map((itm) => {
        const account = itm?.accounts?.[0] || {};
        return {
          value: itm.id,
          label: `${itm.bank_name} - ₹${account.balance ?? 0}`,
          account_number: account.account_number,
          balance: account.balance,
        };
      });

      setAllBanksData(rData);
      return rData;
    } else {
      setAllBanksData([]);
      return [];
    }
  };

  const handleUpdate = (data) => {
    setStoreId(data);
    setShowRejectAlert(true);
  };

  const handleRejected = async (values, { setSubmitting, resetForm }) => {
    if (!canApprove) {
      toast.error(APPROVE_DENIED_MESSAGE);
      return;
    }
    const sData = {
      id: storeId?.id,
      rejected_remarks: values?.remark,
      status: "2",
    };
    const res = await postRejectStockRequest(sData);
    if (res.status) {
      toast.success(res.message);
      navigate(-1);
    } else {
      toast.error(res.message);
    }
    resetForm();
    setSubmitting(false);
    setShowRejectAlert(false);
  };

  // const handleAccountByBankIdChange = async (val, id, setFieldValue) => {
  //   if (setFieldValue) {
  //     setFieldValue("bank_id", val);
  //     setFieldValue(`account_id`, null);
  //     setAccountBalance({ balance: 0 });
  //   }
  //   if (!val) return false;
  //   setAllAccountByBankId([]);
  //   fetchAllAccountByBankId(val);
  // };

  // const fetchAllAccountByBankId = async (id) => {
  //   const res = await getAllAccountByBankId(id);
  //   if (res.status) {
  //     const rData = res?.data?.map((itm) => {
  //       return {
  //         value: itm.id,
  //         label: itm.account_number,
  //         account_holder_name: itm?.account_holder_name,
  //         account_type: itm?.account_type,
  //         logo: itm.res?.data?.bank_logo,
  //         balance: itm.balance,
  //       };
  //     });
  //     setAllAccountByBankId(rData);
  //   } else {
  //     setAllAccountByBankId([]);
  //   }
  // };

  // const fetchAllAccountByBankId = async (bankId) => {
  //   const res = await getAllAccountByBankId(bankId,user_id);

  //   if (res.status) {
  //     const rData = res.data.map(itm => ({
  //       value: itm.id,
  //       label: itm.account_number,
  //       account_holder_name: itm.account_holder_name,
  //       account_type: itm.account_type,
  //       balance: itm.balance,
  //       branch: itm.branch,   // ✅ ADD THIS
  //     }));

  //     setAllAccountByBankId(rData);
  //   } else {
  //     setAllAccountByBankId([]);
  //   }
  // };

  const handleAccountByBankIdChange = async (val) => {
    if (!val) return;
    setAllAccountByBankId([]);
    setAccountBalance({ balance: 0 });
    fetchAllAccountByBankId(val);
  };

  const fetchAllAccountByBankId = async (bankId) => {
    const res = await getAllAccountByBankId(bankId, user_id);

    if (res.status) {
      const rData = res.data.map((itm) => ({
        value: itm.id,
        label: itm.account_number,
        account_holder_name: itm.account_holder_name,
        balance: itm.balance,
        branch: itm.branch,
      }));

      setAllAccountByBankId(rData);
    } else {
      setAllAccountByBankId([]);
    }
  };

  //  const parseCustomDate = (dateStr) => {
  //   // "24-11-2025 11:21:53 AM"
  //   const [datePart, timePart, ampm] = dateStr.split(" ");
  //   const [day, month, year] = datePart.split("-").map(Number);
  //   let [hours, minutes, seconds] = timePart.split(":").map(Number);

  //   // Convert to 24-hour format
  //   if (ampm === "PM" && hours !== 12) hours += 12;
  //   if (ampm === "AM" && hours === 12) hours = 0;

  //   return new Date(year, month - 1, day, hours, minutes, seconds);
  // };

  const parseCustomDate = (dateStr) => {
    if (!dateStr) return new Date();

    const [datePart, timePart, ampm] = dateStr.split(" ");

    const [year, month, day] = datePart.split("-").map(Number);
    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes, seconds);
  };

  const userFormatOptionLabel = ({ label, logo }) => (
    <div>
      {logo ? (
        <img
          src={process.env.REACT_APP_API_URL + logo}
          className="avatar me-2"
        />
      ) : null}
      {label}
    </div>
  );

  useEffect(() => {
    if (id !== "new") {
      fetchSingleData();
    }
    fetchItemMasterData();
    fetchAllBanksData();
  }, [refresh]);

  // const isUser = userType === "user";
  // const isSupplier = userType === "supplier";

  // const isUser = props.values.user_type === "user";
  // const isSupplier = props.values.user_type === "supplier";

  const userDetails = {
    name: edit?.request_for || "-",
    fundLimit: edit?.requested_for_credit_limit || "0.00",
    requestedAmount: edit?.requested_amount || "0.00",
  };

  const supplierDetails = {
    name: edit?.supplier_id?.label || "-",
    gst: edit?.supplier_gst || "-",
  };

  // const bankDetails = {
  //   bankName: props.values.user_id?.label || "-",
  //   holder: accountBalance?.account_holder_name || "-",
  //   accountNo: accountBalance?.label || "-",
  //   balance: accountBalance?.balance || "0.00",
  //   branch: accountBalance?.branch || "-",
  // };

  return (
    <>
      <Helmet>
        <title>Transfer Stock Request · CMS Electricals </title>
      </Helmet>
      <Col md={12} data-aos={"fade-up"} data-aos-delay={200}>
        <CardComponent
          showCardHeader={true}
          className={type === "view" && "after-bg-light"}
          title={`${
            type == "update"
              ? "Update transfer stock"
              : "Transfer Stock Request"
          } `}
          heading2={
            edit?.id ? (
              <div>
                &nbsp;For :{" "}
                {/* The detail API names these requested_for_*; the old
                    request_for_* keys never arrived, so the heading showed
                    only a default photo and the request number. */}
                <ImageViewer
                  src={
                    forImage
                      ? `${process.env.REACT_APP_API_URL}${forImage}`
                      : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                  }
                >
                  <img
                    width={35}
                    height={35}
                    className="my-bg object-fit p-1 rounded-circle"
                    src={
                      forImage
                        ? `${process.env.REACT_APP_API_URL}${forImage}`
                        : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                    }
                  />{" "}
                </ImageViewer>{" "}
                {forName || "-"}
                {forEmpId ? ` (${forEmpId})` : ""} - {edit?.unique_id}
              </div>
            ) : null
          }
        >
          {type == "view" ? (
            <ViewStockRequestDetails edit={edit} />
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                user_type: "",
                user_id: "",
                id: "",
                remark: "",
                transaction_id: "",
                bill_number: edit?.bill_number || edit?.bill_no || "",
                bill_date: edit?.bill_date || "",
                payment_mode: "",
                account_id: "",
                //               stock_transfer_date:
                // edit?.stock_transfer_date ||
                // (edit?.request_date
                //   ? new Date(edit.request_date.replace(" AM", "").replace(" PM", ""))
                //       .toISOString()
                //       .split("T")[0]
                //   : ""),
                // Never pre-filled — user must pick. Only a genuinely-saved
                // transfer date (true edit) is kept; request_date is NOT used.
                stock_transfer_date: edit?.stock_transfer_date
                  ? parseCustomDate(edit.stock_transfer_date)
                  : "",

                transfer_data: [
                  {
                    new_request_stock: (
                      edit?.approved_data?.[0]?.new_request_stock || []
                    ).map((item) => ({
                      ...item,
                      transfer_qty: item.transfer_qty ?? 0,
                    })) || [
                      {
                        item_name: "",
                        description: "",
                        current_user_stock: "",
                        previous_price: "",
                        current_price: "",
                        request_quantity: "",
                        fund_amount: "",
                        price: "",
                        quantity: "",
                        transfer_qty: 0,
                        request_transfer_fund: "",
                        remaining_quantity: "",
                      },
                    ],
                    request_stock: (
                      edit?.approved_data?.[0]?.request_stock || []
                    ).map((item) => ({
                      ...item,
                      transfer_qty: item.transfer_qty ?? 0,
                    })) || [
                      {
                        item_name: "",
                        description: "",
                        current_user_stock: "",
                        previous_price: "",
                        current_price: "",
                        request_quantity: "",
                        fund_amount: "",
                        price: "",
                        quantity: "",
                        transfer_qty: 0,
                        request_transfer_fund: "",
                        remaining_quantity: "",
                      },
                    ],
                  },
                ],
              }}
              validationSchema={
                type == "transfer" && !showRescheduled
                  ? TransferStockSchema
                  : null
              }
              onSubmit={handleSubmit}
            >
              {(props) => {
                console.log("BANK ID", props.values.user_id?.value);
                console.log("ACCOUNTS", allAccountByBankId);
                console.log("ACCOUNT SELECTED", accountBalance);

                const bankDetails = {
                  bankName: props.values.user_id?.label || "-",
                  holder: accountBalance?.account_holder_name || "-",
                  accountNo: props.values.account_id?.label || "-",
                  balance: accountBalance?.balance || "0.00",
                  branch: accountBalance?.branch || "-",
                };

                return (
                  <Form onSubmit={props?.handleSubmit}>
                    <Row className="g-3">
                      {/* <Form.Group className="mb-3" as={Col} md={3}>
  <Form.Label>User Type</Form.Label>
  <Select
    menuPortalTarget={document.body}
    placeholder="Select User Type"
    options={userTypeOptions}
    value={userTypeOptions.find(
      (opt) => opt.value === userType
    )}
    onChange={(e) => {
      setUserType(e.value);

      // optional (recommended)
      // bank / account reset if needed
      // props.setFieldValue("user_id", "");
      // props.setFieldValue("account_id", "");
    }}
  />

   <Field name="user_type">
    {({ field, form }) => (
      <Select
        menuPortalTarget={document.body}
        placeholder="Select User Type"
        options={userTypeOptions}
        isClearable
        value={
          field.value
            ? userTypeOptions.find(opt => opt.value === field.value)
            : null
        }
        onChange={(option) => {
          form.setFieldValue(
            "user_type",
            option ? option.value : ""
          );

          // dependent reset
          form.setFieldValue("user_id", "");
          form.setFieldValue("account_id", "");
          setAccountBalance({ balance: 0 });
        }}
      />
    )}
  </Field>

                    </Form.Group> */}

                      {/* <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label> Payment Mode</Form.Label>
                        <Select
                          menuPortalTarget={document.body}
                          // value={props.values?.payment_mode}
                          placeholder="Payment Mode"
                          name={`payment_mode`}
                          options={paymentMode}
                          isDisabled={type == "update"}
                          onChange={(e) => {
                            props.setFieldValue(`payment_mode`, e.value);
                          }}
                          // formatOptionLabel={userFormatOptionLabel}
                        />
                      </Form.Group> */}
                      <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>{t("Payment Mode")}</Form.Label>
                        {/* <Select
                          menuPortalTarget={document.body}
                          // value={props.values?.payment_mode}
                          placeholder="Payment Mode"
                          name={`payment_mode`}
                          options={paymentMode}
                          onChange= { async (e) => {
                            props.setFieldValue(`payment_mode`, e.value);
                            props.setFieldValue(`user_id`, "");

                      const data =    await  fetchAllBanksData();
                           if(e.label  == "CASH"){
        const cashAccount = data.find((item) =>
          item.label.toLowerCase().includes("cash account")
        );
      if (cashAccount) {
  props.setFieldValue("user_id", cashAccount);

  // 🔥 TRIGGER the onChange logic manually
  handleAccountByBankIdChange(
    cashAccount.value,
    id,
    props.setFieldValue
  );
}

      }

                            // props.setFieldValue('user_id' , allBanksData.find((item) => item.label === "Cash Account")?.value);
                          }}

                          // formatOptionLabel={userFormatOptionLabelAccount
                        /> */}
                        <Select
                          placeholder="Payment Mode"
                          options={paymentMode}
                          onChange={async (e) => {
                            // 1️⃣ set payment mode
                            props.setFieldValue("payment_mode", e.value);

                            // 2️⃣ reset
                            props.setFieldValue("user_id", "");
                            props.setFieldValue("account_id", "");
                            setAllAccountByBankId([]);
                            setAccountBalance({});

                            // 3️⃣ load banks
                            const banks = await fetchAllBanksData();

                            // 4️⃣ CASH logic (🔥 MOST IMPORTANT)
                            if (e.value === "CASH") {
                              const cashBank = banks.find((b) =>
                                b.label.toLowerCase().includes("cash"),
                              );

                              if (cashBank) {
                                // ✅ bank set
                                props.setFieldValue("user_id", cashBank);

                                // ✅ fetch accounts of cash bank
                                const res = await getAllAccountByBankId(
                                  cashBank.value,
                                  user_id,
                                );

                                if (res.status && res.data.length > 0) {
                                  const cashAccount = {
                                    value: res.data[0].id,
                                    label: res.data[0].account_number,
                                    account_holder_name:
                                      res.data[0].account_holder_name,
                                    balance: res.data[0].balance,
                                    branch: res.data[0].branch,
                                  };

                                  // 🔥🔥 THIS WAS MISSING
                                  props.setFieldValue(
                                    "account_id",
                                    cashAccount,
                                  );
                                  setAccountBalance(cashAccount);
                                }
                              }
                            }
                          }}
                        />
                      </Form.Group>
                      {/* <Form.Group as={Col} md={3}>
                        <Form.Label> Bank</Form.Label>
                        <Select
                          menuPortalTarget={document.body}
                          value={props.values.user_id}
                          placeholder="Select Bank"
                          name={`user_id`}
                          options={allBanksData}
                          isDisabled={
                            props.values.payment_mode == "Cash" ||
                            type == "update"
                          }
                          onChange={(e) => {
                            handleAccountByBankIdChange(
                              e.value,
                              id,
                              props.setFieldValue,
                            );
                            props.setFieldValue(`user_id`, e);
                          }}
                          formatOptionLabel={userFormatOptionLabel}
                        />
                        <ErrorMessage
                          name={`user_id`}
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}
                      <Form.Group as={Col} md={3}>
                        <Form.Label> {t("Bank")}</Form.Label>

                        {/* <Select
                          menuPortalTarget={document.body}
                          value={props.values.user_id}
                          //                         value={
                          //   props.values.payment_mode ===
                          //     paymentMode.find((e) => e.label === "CASH")?.value
                          //     ? allBanksData.find((item) => item.label === "Cash Account") || null
                          //     : allBanksData.find((item) => item.value === props.values.user_id) || null
                          // }

                          // isDisabled={props.values.payment_mode === "CASH"}
                          isDisabled={!props.values.payment_mode}

                          placeholder="Select Bank"
                          name={`user_id`}
             options={
  paymentMode.find((e) => e.label === "CASH")?.value === props.values.payment_mode
    ? allBanksData.filter((item) => item.label.includes("Cash Account"))
    : allBanksData.filter((item) => !item.label.includes("Cash Account"))
}

                          onChange={(e) => {
                            handleAccountByBankIdChange(
                              e.value,
                              id,
                              props.setFieldValue
                            );
                            props.setFieldValue(`user_id`, e);
                            // props.setFieldValue(`account_id`, null);
                          }}
                          formatOptionLabel={userFormatOptionLabel}
                        /> */}
                        <Select
                          value={props.values.user_id}
                          placeholder="Select Bank"
                          isDisabled={!props.values.payment_mode}
                          options={
                            props.values.payment_mode === "CASH"
                              ? allBanksData.filter((b) =>
                                  b.label.toLowerCase().includes("cash"),
                                )
                              : allBanksData.filter(
                                  (b) =>
                                    !b.label.toLowerCase().includes("cash"),
                                )
                          }
                          onChange={(e) => {
                            props.setFieldValue("user_id", e);
                            props.setFieldValue("account_id", "");
                            setAllAccountByBankId([]);
                            setAccountBalance({}); // 🔥 IMPORTANT

                            handleAccountByBankIdChange(
                              e.value,
                              id,
                              props.setFieldValue,
                            );
                          }}
                        />
                      </Form.Group>
                      {/* <Form.Group as={Col} md={3}>
                        <Form.Label>Account Number</Form.Label>
                        <Select
                          menuPortalTarget={document.body}
                          // value={main?.account_id}
                          placeholder="Select Account"
                          name={`account_id`}
                          options={allAccountByBankId}
                          isDisabled={
                            props.values.payment_mode == "Cash" ||
                            type == "update"
                          }
                          onChange={(e) => {
                            setAccountBalance(e);
                            props.setFieldValue(`account_id`, e);
                          }}
                          formatOptionLabel={userFormatOptionLabel}
                        />
                        <ErrorMessage
                          name={`account_id`}
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}

                      <Form.Group as={Col} md={3}>
                        <Form.Label> {t("Account No")}.</Form.Label>
                        {/* <Select
                          menuPortalTarget={document.body}
                          // value={main?.account_id}
                          placeholder="Select Account"
                          name={`account_id`}
                          options={allAccountByBankId}
                          // isDisabled={props.values.payment_mode === "CASH"}
                          isDisabled={!props.values.payment_mode}
                          onChange={(e) => {
                            setAccountBalance(e);
                            props.setFieldValue(`account_id`, e);
                          }}
                          formatOptionLabel={userFormatOptionLabel}
                        /> */}
                        <Select
                          placeholder="Select Account"
                          options={allAccountByBankId}
                          isDisabled={!props.values.user_id}
                          onChange={(e) => {
                            props.setFieldValue("account_id", e);
                            setAccountBalance(e); // 🔥 IMPORTANT
                          }}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label> {t("Stock Tranfer Date")}</Form.Label>
                        <DatePickercomp
                          name="stock_transfer_date"
                          // value={props.values.stock_transfer_date}
                          value={
                            props.values.stock_transfer_date instanceof Date
                              ? props.values.stock_transfer_date
                              : null
                          }
                          sendDate={(value) => {
                            if (value) {
                              props.setFieldValue(
                                "stock_transfer_date",
                                new Date(value),
                              );
                              // value is "YYYY-MM-DD" — drive the existing-items
                              // re-fetch so the list is scoped to this date.
                              setTransferDateForItems(value);
                            }
                          }}
                          onBlur={props.handleBlur}
                          label={t("Select Fund Date")}
                          // Can't transfer stock before it was approved/funded.
                          // Floor = the stock request's APPROVE date (approved_at,
                          // format "YYYY-MM-DD HH:mm:ss A"). Fall back to request_date
                          // only if approved_at isn't available. Skip the limit if
                          // missing / unparseable / future (never lock the picker).
                          minDate={(() => {
                            const src = edit?.approved_at || edit?.request_date;
                            if (!src) return null;
                            const datePart = String(src).split(" ")[0];
                            const parts = datePart.split("-");
                            if (parts.length !== 3) return null;
                            const [y, m, d] = parts.map(Number);
                            const dt = new Date(y, m - 1, d);
                            if (isNaN(dt.getTime())) return null;
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return dt > today ? null : dt;
                          })()}
                          isInvalid={Boolean(
                            props.touched.stock_transfer_date &&
                            props.errors.stock_transfer_date,
                          )}
                        />
                        <ErrorMessage
                          name="stock_transfer_date"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>

                      {/* <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Remarks</Form.Label>

                        <ReactTextareaAutosize
                          minRows={0}
                          placeholder="type remarks..."
                          onChange={props.handleChange}
                          disabled={type == "update"}
                          // value={props.values.remark}
                          name={"remark"}
                          className="edit-textarea"
                          onBlur={props.handleBlur}
                          // isInvalid={Boolean(props.touched.remark && props.errors.remark)}
                        />
                        <ErrorMessage
                          name="remark"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}

                      {/* <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Transaction Id</Form.Label>

                        <Form.Control
                          type="text"
                          placeholder="Enter Transaction Id"
                          name={`transaction_id`}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          // disabled={type == "update"}
                        />
                        <ErrorMessage
                          name="transaction_id"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}

                      {/* <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Bill Number</Form.Label>

                        <Form.Control
                          type="text"
                          placeholder="Enter Bill No."
                          name={`bill_number`}
                          value={props?.values?.bill_number}
                          onChange={props.handleChange}
                        />
                        <ErrorMessage
                          name="bill_number"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}

                      {/* <Form.Group className="mb-3" as={Col} md={2}>
                        <Form.Label>Bill Date </Form.Label>
                        <Form.Control
                          type="date"
                          name="bill_date"
                          value={props?.values?.bill_date}
                          onChange={props.handleChange}
                        ></Form.Control>
                        <ErrorMessage
                          name="bill_date"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group> */}

                      {/* <Form.Group
                        as={Col}
                        md={4}
                        className="text-end text-gray"
                      >
                        {props.values.payment_mode != "Cash" && (
                          <>
                            A/c Holder -
                            <b className="text-green">
                              {accountBalance?.account_holder_name || "--"}
                            </b>
                            <br />
                            Bank balance{" "}
                            <b className={"text-green"}>
                              : ₹ {accountBalance?.balance || "0.00"}
                            </b>
                          </>
                        )}
                        <br />
                        Previous Stock balance{" "}
                        <b className="text-green">
                          {" "}
                          ₹ {previousStockAmount.toFixed(2) || "0.00"}
                        </b>
                        <br />
                        Supplier Name -{" "}
                        <b className="text-green">{edit?.supplier_id?.label}</b>
                        <br />
                        Fund Limit :
                        <b> ₹ {edit?.requested_for_credit_limit} </b>
                      </Form.Group> */}

                      {/* 🔹 BANK DETAILS (COMMON) */}

                      <Col md={6}>
                        <div className="p-20 shadow rounded h-100">
                          <strong className="text-secondary">
                            Bank Details
                          </strong>

                          <div className="mt-2">
                            <table className="table-sm table">
                              <tbody>
                                <tr>
                                  <th>Bank Name :</th>
                                  <td>{bankDetails.bankName}</td>
                                </tr>
                                <tr>
                                  <th>Account Holder :</th>
                                  <td>{bankDetails.holder}</td>
                                </tr>
                                <tr>
                                  <th>Account No :</th>
                                  <td>{bankDetails.accountNo}</td>
                                </tr>
                                <tr>
                                  <th>Current Balance :</th>
                                  <td>{formatAmountINR(bankDetails.balance)}</td>
                                </tr>
                                <tr>
                                  <th>Branch :</th>
                                  <td>{bankDetails.branch}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </Col>

                      {/* <Col md={6}>
  <div className="p-20 shadow rounded h-100">
    <strong className="text-secondary">
      User Details
    </strong>

    <div className="mt-2">
      <table className="table-sm table">
        <tbody>
          <tr>
            <th>User Name :</th>
            <td>-</td>
          </tr>
          <tr>
            <th>Fund Limit :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Total Transfer Amount :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Total Punched Amount :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Remaining Limit :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Requested Amount :</th>
            <td>₹ 0.00</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</Col> */}

                      <Col md={6}>
                        <div className="p-20 shadow rounded h-100">
                          <strong className="text-secondary">
                            User Details
                          </strong>

                          <div className="mt-2">
                            <table className="table-sm table">
                              <tbody>
                                <tr>
                                  <th>User Name :</th>
                                  <td>
                                    {/* employee id under the name, as on the other screens */}
                                    <div className="d-grid">
                                      <span>{edit?.requested_for_name || "-"}</span>
                                      {edit?.requested_for_employee_id && (
                                        <small className="text-secondary">({edit.requested_for_employee_id})</small>
                                      )}
                                    </div>
                                  </td>
                                </tr>

                                <tr>
                                  <th>Stock Limit :</th>
                                  <td>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span>
                                        {formatAmountINR(edit?.Total_Stock_Limit)}
                                      </span>
                                      <MdEdit
                                        className="ms-3 text-primary"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "18px",
                                        }}
                                        title="Edit Fund Limit"
                                        onClick={() =>
                                          navigate(
                                            `/Employees/AddEmployee/${edit?.requested_for}`,
                                            {
                                              state: {
                                                id: edit?.requested_for,
                                                employee_id:
                                                  edit?.request_for_employee_id,
                                              },
                                            },
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>

                                {/* the user's whole stock, from the ledger */}
                                <tr>
                                  <th>Total Stock Received :</th>
                                  <td>
                                    {formatAmountINR(edit?.total_stock_transferred)}
                                  </td>
                                </tr>

                                <tr>
                                  <th>Total Punched Amount :</th>
                                  <td>
                                    {formatAmountINR(edit?.total_amount_punched)}
                                  </td>
                                </tr>

                                {Number(edit?.stock_given_on) > 0 ? (
                                  <tr>
                                    <th>Given to Others :</th>
                                    <td>{formatAmountINR(edit?.stock_given_on)}</td>
                                  </tr>
                                ) : null}

                                <tr>
                                  <th>Current Stock Balance :</th>
                                  <td className="fw-bold">
                                    {formatAmountINR(edit?.Current_stock_Balance)}
                                  </td>
                                </tr>

                                <tr>
                                  <th>Remaining Limit :</th>
                                  <td>
                                    {formatAmountINR(edit?.Remaining_Stock_Limit)}
                                  </td>
                                </tr>

                                <tr>
                                  <th>Requested Amount :</th>
                                  <td>
                                    {formatAmountINR(
                                      edit?.total_sum_of_request_stock,
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </Col>

                      {/* {props.values.user_type === "supplier" && (
  <Col md={6}>
  <div className="p-20 shadow rounded h-100">
    <strong className="text-secondary">
      Supplier Details
    </strong>

    <div className="mt-2">
      <table className="table-sm table">
        <tbody>
          <tr>
            <th>Supplier Name :</th>
            <td>-</td>
          </tr>
          <tr>
            <th>Fund Limit :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Total Transfer Amount :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Total Punched Amount :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Remaining Limit :</th>
            <td>₹ 0.00</td>
          </tr>
          <tr>
            <th>Requested Amount :</th>
            <td>₹ 0.00</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</Col>
)} */}
                    </Row>

                    <FieldArray name="transfer_data">
                      {() => (
                        <div>
                          {props?.values?.transfer_data.map((main, id) => (
                            <div key={id} className="p-1 mb-2 shadow">
                              <FieldArray name={`transfer_data`}>
                                {({ push: pushFund, remove: removeFund }) => (
                                  <div className="p-2">
                                    <Row className="g-3">
                                      <StockTableView
                                        props={props}
                                        main={main}
                                        id={id}
                                        pushFund={pushFund}
                                        removeFund={removeFund}
                                        edit={edit}
                                        type={type}
                                        itemMasterData={itemMasterData}
                                        setBill_Amount={setBill_Amount}
                                      />

                                      <ConfirmAlert
                                        size={"sm"}
                                        defaultIcon={<BsQuestionLg />}
                                        deleteFunction={props.handleSubmit}
                                        formikProps={props}
                                        hide={setShowAlert}
                                        show={showAlert}
                                        title={`Confirm Transfer`}
                                        description={`Are you sure you want to transfer this!!`}
                                      />
                                      <ConfirmAlert
                                        size={"sm"}
                                        defaultIcon={<BsQuestionLg />}
                                        deleteFunction={props.handleSubmit}
                                        formikProps={props}
                                        hide={setShowRescheduled}
                                        show={showRescheduled}
                                        title={`Confirm Rescheduled`}
                                        description={`Are you sure you want to rescheduled this!!`}
                                        children={
                                          <Row>
                                            <Col md={3}></Col>
                                            <Col
                                              md={4}
                                              className="position-relative"
                                            >
                                              <input
                                                type="date"
                                                // value={props.values.rescheduled_date}
                                                onChange={(e) =>
                                                  props.setFieldValue(
                                                    "rescheduled_date",
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </Col>
                                          </Row>
                                        }
                                      />
                                    </Row>
                                  </div>
                                )}
                              </FieldArray>
                            </div>
                          ))}
                        </div>
                      )}
                    </FieldArray>

                    {/* Auto-calculated grand total of every transfer row's
                        per-row "Transfer Amount" (transfer_data[*].request_stock
                        + new_request_stock → transfer_amount). Computed straight
                        from props.values on each render, so it updates live as
                        the user changes any TRANSFER QTY / amount. Empty/NaN
                        rows are treated as 0. */}
                    <div className="d-flex justify-content-end mt-3">
                      <strong>
                        Total Transfer Amount :{" "}
                        {formatAmountINR(
                          Number(
                            (props?.values?.transfer_data || [])
                              .reduce((grand, main) => {
                                const stockSum = (main?.request_stock || []).reduce(
                                  (t, itm) => t + (Number(itm?.transfer_amount) || 0),
                                  0,
                                );
                                const newStockSum = (
                                  main?.new_request_stock || []
                                ).reduce(
                                  (t, itm) => t + (Number(itm?.transfer_amount) || 0),
                                  0,
                                );
                                return grand + stockSum + newStockSum;
                              }, 0)
                              .toFixed(2),
                          ),
                        )}
                      </strong>
                    </div>

                    <Row className="g-3 mt-4 border-top pt-3">
                      <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Remarks</Form.Label>

                        {/* <ReactTextareaAutosize
                          minRows={0}
                          placeholder="type remarks..."
                          onChange={props.handleChange}
                          disabled={type == "update"}
                          // value={props.values.remark}
                          name={"remark"}
                          className="edit-textarea"
                          onBlur={props.handleBlur}
                        // isInvalid={Boolean(props.touched.remark && props.errors.remark)}
                        /> */}

                        <Form.Control
                          type="text"
                          placeholder="TYPE REMARKS..."
                          name={"remark"}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          // disabled={type == "update"}
                        />

                        <ErrorMessage
                          name="remark"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Transaction Id</Form.Label>

                        <Form.Control
                          type="text"
                          placeholder="Enter Transaction Id"
                          name={`transaction_id`}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          // disabled={type == "update"}
                        />
                        <ErrorMessage
                          name="transaction_id"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} md={3}>
                        <Form.Label>Bill Number</Form.Label>

                        <Form.Control
                          type="text"
                          placeholder="Enter Bill No."
                          name={`bill_number`}
                          value={props?.values?.bill_number}
                          onChange={props.handleChange}
                        />
                        <ErrorMessage
                          name="bill_number"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" as={Col} md={2}>
                        <Form.Label>Bill Date </Form.Label>
                        <Form.Control
                          type="date"
                          name="bill_date"
                          value={props?.values?.bill_date}
                          onChange={props.handleChange}
                        ></Form.Control>
                        <ErrorMessage
                          name="bill_date"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>
                    </Row>
                    {/* <GroupTable data={allOldItems} /> */}

                    <Form.Group as={Col} md={12}>
                      <div className="my-4 text-center">
                        {/* {type === "approve" ? ( */}
                        <button
                          type={`button`}
                          onClick={() => setShowRescheduled(true)}
                          className="shadow me-3 border-0 danger-combo cursor-pointer px-4 py-1"
                        >
                          Re-schedule
                        </button>
                        {/* ) : null} */}

                        {edit?.id && true && (
                          <button
                            type={`${edit?.id ? "button" : "submit"}`}
                            onClick={() => setShowAlert(edit?.id && true)}
                            className={`shadow border-0 purple-combo  cursor-pointer px-4 py-1`}
                          >
                            {props?.isSubmitting ? (
                              <>
                                <Spinner
                                  animation="border"
                                  variant="primary"
                                  size="sm"
                                />
                                PLEASE WAIT...
                              </>
                            ) : (
                              <>
                                {edit?.id && type == "transfer"
                                  ? "Transfer"
                                  : type == "update"
                                    ? "update"
                                    : "SAVE"}
                              </>
                            )}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => handleUpdate(edit)}
                          className="shadow border-0 red-combo cursor-pointer px-4 py-1 mx-3"
                        >
                          reject
                        </button>
                      </div>
                    </Form.Group>
                    {/* Existing-items picker is gated on the transfer date:
                        the user must pick a transfer date first, then the list
                        loads scoped to items received on/before that date. */}
                    {transferDateForItems ? (
                      <GroupTable data={allOldItems} />
                    ) : (
                      <div className="p-3 text-center text-secondary shadow">
                        Please select the stock transfer date first to view
                        existing items.
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          )}

          <Formik
            enableReinitialize={true}
            initialValues={{
              remark: "",
            }}
            validationSchema={addRemarkSchema}
            onSubmit={handleRejected}
          >
            {(props) => (
              <ConfirmAlert
                formikProps={props}
                size={"md"}
                hide={setShowRejectAlert}
                show={showRejectAlert}
                type="submit"
                title={"Confirm Reject"}
                description={
                  <Row className="g-3 py-1">
                    <Col md={12}>
                      <TextareaAutosize
                        minRows={3}
                        placeholder="type remarks..."
                        onChange={props.handleChange}
                        name="remark"
                        className="edit-textarea"
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.remark && props.errors.remark,
                        )}
                      />
                      <small className="text-danger">
                        {props.errors.remark}
                      </small>
                    </Col>

                    <Col md={12}>
                      <div className="table-scroll">
                        {storeId?.request_stock?.request_stock.length > 0 && (
                          <>
                            <h6 className="my-2">Request Stock</h6>
                            <Table className="table-sm table Roles">
                              <thead>
                                <tr>
                                  <th>Unique Id</th>
                                  <th>Item Name</th>
                                  <th>Item rate</th>
                                  <th>request qty</th>
                                  <th>request amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {storeId?.request_stock?.request_stock?.map(
                                  (itm, idx) => (
                                    <tr key={idx}>
                                      <td>{storeId?.unique_id ?? "--"}</td>
                                      <td>
                                        <div className="d-flex">
                                          <ImageViewer
                                            src={
                                              itm?.item_name?.image
                                                ? `${process.env.REACT_APP_API_URL}${itm?.item_name?.image}`
                                                : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                                            }
                                          >
                                            <img
                                              width={35}
                                              height={35}
                                              className="my-bg object-fit p-1 rounded-circle"
                                              src={
                                                itm?.item_name?.image
                                                  ? `${process.env.REACT_APP_API_URL}${itm?.item_name?.image}`
                                                  : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                                              }
                                            />
                                          </ImageViewer>{" "}
                                          <span className="small d-grid">
                                            <span>{itm.item_name?.label}</span>
                                            <span className="text-gray">
                                              {itm.item_name?.unique_id
                                                ? `(${itm.item_name?.unique_id})`
                                                : "-"}
                                            </span>
                                          </span>
                                        </div>
                                      </td>
                                      <td>{formatAmountINR(itm.item_name?.rate)}</td>
                                      <td>{to3(itm.request_quantity)}</td>
                                      <td>{formatAmountINR(itm.total_price)}</td>
                                    </tr>
                                  ),
                                )}
                                <tr>
                                  <td colSpan={2}></td>
                                  <td colSpan={2}>total request Quantity</td>
                                  <td className="text-start text-green">
                                    <b> {to3(storeId?.total_request_quantity)}</b>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </>
                        )}

                        {storeId?.request_stock?.new_request_stock.length >
                          0 && (
                          <>
                            <h6 className="my-2">New Request Stock</h6>
                            <Table className="table-sm table Roles">
                              <thead>
                                <tr>
                                  <th>Unique Id</th>
                                  <th>Item Name</th>
                                  <th>Item rate</th>
                                  <th>request qty</th>
                                  <th>request amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {storeId?.request_stock?.new_request_stock?.map(
                                  (itm, idx) => (
                                    <tr key={idx}>
                                      <td>{storeId?.unique_id ?? "--"}</td>
                                      <td>
                                        <div className="d-flex">
                                          <ImageViewer
                                            src={
                                              itm?.item_image
                                                ? `${process.env.REACT_APP_API_URL}${itm?.item_image}`
                                                : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                                            }
                                          >
                                            <img
                                              width={35}
                                              height={35}
                                              className="my-bg object-fit p-1 rounded-circle"
                                              src={
                                                itm?.item_image
                                                  ? `${process.env.REACT_APP_API_URL}${itm?.item_image}`
                                                  : `${process.env.REACT_APP_API_URL}/assets/images/default-image.png`
                                              }
                                            />
                                          </ImageViewer>{" "}
                                          <span className="small d-grid">
                                            <span>{itm.title?.label}</span>
                                            <span className="text-gray">
                                              {itm.item_name?.unique_id
                                                ? `(${itm.item_name?.unique_id})`
                                                : "-"}
                                            </span>
                                          </span>
                                        </div>
                                      </td>
                                      <td>{formatAmountINR(itm?.rate)}</td>
                                      <td>{to3(itm?.qty)}</td>
                                      <td>{formatAmountINR(itm?.fund_amount)}</td>
                                    </tr>
                                  ),
                                )}
                                <tr>
                                  <td colSpan={2}></td>
                                  <td colSpan={2}>total request Quantity.</td>
                                  <td className="text-start text-green">
                                    <b>{to3(storeId?.total_new_request_quantity)}</b>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </>
                        )}
                      </div>
                    </Col>
                  </Row>
                }
              />
            )}
          </Formik>
        </CardComponent>
      </Col>
    </>
  );
};

export default CreateStockTransfer;
